import { useRef, useEffect, useCallback } from "react";
import type { GraphNode, GraphEdge } from "@/hooks/useAdminBrain";

interface D3ForceLink {
  id: (fn: (d: SimNode) => number) => D3ForceLink;
  distance: (v: number) => D3ForceLink;
  strength: (fn: (e: GraphEdge) => number) => D3ForceLink;
}
interface D3ForceLib {
  forceSimulation: (nodes: SimNode[]) => D3Simulation;
  forceManyBody: () => { strength: (v: number) => unknown };
  forceLink: (edges: GraphEdge[]) => D3ForceLink;
  forceX: (v: number) => { strength: (v: number) => unknown };
  forceY: (v: number) => { strength: (v: number) => unknown };
  forceCollide: () => { radius: (fn: (d: SimNode) => number) => unknown };
}
interface D3Simulation {
  force: (name: string, force: unknown) => D3Simulation;
  alphaDecay: (v: number) => D3Simulation;
  on: (event: string, fn: () => void) => D3Simulation;
  stop: () => void;
}
declare global {
  interface Window {
    d3?: D3ForceLib;
  }
}

export const BRAIN_CATEGORY_COLORS: Record<string, string> = {
  calibration_ad: "#8B5CF6",
  rubric_rule: "#14B8A6",
  simulation_pattern: "#EC4899",
  real_performance: "#F87171",
  accuracy_signal: "#F59E0B",
  admin_pin: "#9CA3AF",
};

interface SimNode extends GraphNode {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

interface PredictionBrainGraphProps {
  nodes: GraphNode[];
  edges: GraphEdge[];
  selectedNodeId: number | null;
  onSelectNode: (id: number | null) => void;
  searchTerm: string;
}

export function PredictionBrainGraph({ nodes, edges, selectedNodeId, onSelectNode, searchTerm }: PredictionBrainGraphProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const simNodesRef = useRef<SimNode[]>([]);
  const simEdgesRef = useRef<GraphEdge[]>([]);
  const simulationRef = useRef<any>(null);
  const transformRef = useRef({ x: 0, y: 0, scale: 1 });
  const hoveredNodeRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const lastTransformRef = useRef({ x: 0, y: 0 });
  const animFrameRef = useRef<number>(0);
  const selectedRef = useRef<number | null>(null);
  const searchRef = useRef("");

  selectedRef.current = selectedNodeId;
  searchRef.current = searchTerm;

  const getConnectedIds = useCallback((nodeId: number | null): Set<number> => {
    if (!nodeId) return new Set();
    const ids = new Set<number>();
    for (const e of simEdgesRef.current) {
      const srcId = typeof e.source === "object" ? (e.source as GraphNode).id : e.source as number;
      const tgtId = typeof e.target === "object" ? (e.target as GraphNode).id : e.target as number;
      if (srcId === nodeId) ids.add(tgtId);
      if (tgtId === nodeId) ids.add(srcId);
    }
    return ids;
  }, []);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const w = canvas.width;
    const h = canvas.height;
    const { x: tx, y: ty, scale } = transformRef.current;
    const simNodes = simNodesRef.current;
    const simEdges = simEdgesRef.current;
    const selId = selectedRef.current;
    const hovId = hoveredNodeRef.current;
    const sLower = searchRef.current.toLowerCase();
    const connected = getConnectedIds(selId);

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = "#FAF9F6";
    ctx.fillRect(0, 0, w, h);

    ctx.fillStyle = "rgba(140,133,120,0.15)";
    const gridSize = 30 * scale * 2;
    const offsetX = (tx * 2) % gridSize;
    const offsetY = (ty * 2) % gridSize;
    for (let gx = offsetX; gx < w; gx += gridSize) {
      for (let gy = offsetY; gy < h; gy += gridSize) {
        ctx.beginPath();
        ctx.arc(gx, gy, 1, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.save();
    ctx.scale(2, 2);
    ctx.translate(tx, ty);
    ctx.scale(scale, scale);

    for (const e of simEdges) {
      const s = typeof e.source === "object" ? e.source as SimNode : null;
      const t = typeof e.target === "object" ? e.target as SimNode : null;
      if (!s || !t || s.x == null || t.x == null) continue;

      let alpha = 0.12;
      if (selId) {
        if ((s as GraphNode).id === selId || (t as GraphNode).id === selId) alpha = 0.5;
        else alpha = 0.04;
      }

      ctx.beginPath();
      ctx.moveTo(s.x, s.y);
      ctx.lineTo(t.x, t.y);
      ctx.strokeStyle = `rgba(116,111,102,${alpha})`;
      ctx.lineWidth = Math.max(0.5, e.strength * 2);
      ctx.stroke();
    }

    for (const n of simNodes) {
      const color = BRAIN_CATEGORY_COLORS[n.category] || "#6B7280";
      const importance = n.importance ?? 5;
      const radius = 4 + (importance / 10) * 8;
      const isSelected = n.id === selId;
      const isConnected = connected.has(n.id);
      const isHovered = n.id === hovId;

      let nodeAlpha = 1;
      if (selId && !isSelected && !isConnected) nodeAlpha = 0.2;
      if (sLower && !n.title.toLowerCase().includes(sLower)) nodeAlpha = 0.1;

      ctx.globalAlpha = nodeAlpha;

      if (isSelected || isHovered) {
        ctx.beginPath();
        ctx.arc(n.x, n.y, radius + 5, 0, Math.PI * 2);
        ctx.fillStyle = `${color}25`;
        ctx.fill();
      }

      const gradient = ctx.createRadialGradient(n.x - radius * 0.3, n.y - radius * 0.3, 0, n.x, n.y, radius);
      gradient.addColorStop(0, `${color}ff`);
      gradient.addColorStop(1, `${color}99`);

      ctx.beginPath();
      ctx.arc(n.x, n.y, radius, 0, Math.PI * 2);
      ctx.fillStyle = gradient;
      ctx.fill();

      if (isSelected) {
        ctx.strokeStyle = "#1A1A1A";
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      if ((isHovered || isSelected || isConnected) && nodeAlpha > 0.4) {
        ctx.fillStyle = "rgba(26,26,26,0.9)";
        ctx.font = "600 11px Inter, sans-serif";
        ctx.textAlign = "center";
        const label = n.title.length > 28 ? n.title.slice(0, 26) + "…" : n.title;
        const textWidth = ctx.measureText(label).width;
        const px = 5, py = 4;
        const bx = n.x - textWidth / 2 - px;
        const by = n.y - radius - 18 - py;
        ctx.fillStyle = "rgba(255,255,255,0.95)";
        ctx.beginPath();
        ctx.roundRect(bx, by, textWidth + px * 2, 16 + py * 2, 4);
        ctx.fill();
        ctx.fillStyle = "rgba(26,26,26,0.9)";
        ctx.fillText(label, n.x, n.y - radius - 7);
      }

      ctx.globalAlpha = 1;
    }

    ctx.restore();
  }, [getConnectedIds]);

  const scheduleRedraw = useCallback(() => {
    cancelAnimationFrame(animFrameRef.current);
    animFrameRef.current = requestAnimationFrame(draw);
  }, [draw]);

  useEffect(() => {
    scheduleRedraw();
  }, [selectedNodeId, searchTerm, scheduleRedraw]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    const w = container.clientWidth;
    const h = container.clientHeight;
    canvas.width = w * 2;
    canvas.height = h * 2;
    canvas.style.width = `${w}px`;
    canvas.style.height = `${h}px`;

    const simNodes: SimNode[] = nodes.map((n) => {
      const existing = simNodesRef.current.find(sn => sn.id === n.id);
      return {
        ...n,
        x: existing?.x ?? w / 2 + (Math.random() - 0.5) * w * 0.6,
        y: existing?.y ?? h / 2 + (Math.random() - 0.5) * h * 0.6,
        vx: 0,
        vy: 0,
      };
    });

    const simEdges: GraphEdge[] = edges.map(e => ({ ...e }));
    simNodesRef.current = simNodes;
    simEdgesRef.current = simEdges;

    if (simulationRef.current) simulationRef.current.stop();

    const d3 = window.d3;
    if (d3?.forceSimulation) {
      const simulation = d3.forceSimulation(simNodes)
        .force("charge", d3.forceManyBody().strength(-140))
        .force("link", d3.forceLink(simEdges).id((d: SimNode) => d.id).distance(100).strength((e: GraphEdge) => e.strength * 0.3))
        .force("x", d3.forceX(w / 2).strength(0.05))
        .force("y", d3.forceY(h / 2).strength(0.05))
        .force("collide", d3.forceCollide().radius((d: SimNode) => 10 + ((d.importance ?? 5) / 10) * 10))
        .alphaDecay(0.03)
        .on("tick", scheduleRedraw);

      simulationRef.current = simulation;
    }

    draw();
    return () => { if (simulationRef.current) simulationRef.current.stop(); };
  }, [nodes, edges]);

  const screenToWorld = useCallback((sx: number, sy: number) => {
    const { x: tx, y: ty, scale } = transformRef.current;
    return { x: (sx - tx) / scale, y: (sy - ty) / scale };
  }, []);

  const findNodeAt = useCallback((sx: number, sy: number): SimNode | null => {
    const { x: wx, y: wy } = screenToWorld(sx, sy);
    for (let i = simNodesRef.current.length - 1; i >= 0; i--) {
      const n = simNodesRef.current[i];
      const importance = n.importance ?? 5;
      const radius = 4 + (importance / 10) * 8;
      const dx = n.x - wx, dy = n.y - wy;
      if (dx * dx + dy * dy <= (radius + 5) * (radius + 5)) return n;
    }
    return null;
  }, [screenToWorld]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    isDraggingRef.current = true;
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    lastTransformRef.current = { x: transformRef.current.x, y: transformRef.current.y };
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const sx = e.clientX - rect.left, sy = e.clientY - rect.top;
    if (isDraggingRef.current) {
      const dx = e.clientX - dragStartRef.current.x;
      const dy = e.clientY - dragStartRef.current.y;
      transformRef.current.x = lastTransformRef.current.x + dx;
      transformRef.current.y = lastTransformRef.current.y + dy;
      scheduleRedraw();
      return;
    }
    const node = findNodeAt(sx, sy);
    const newId = node?.id ?? null;
    if (hoveredNodeRef.current !== newId) {
      hoveredNodeRef.current = newId;
      scheduleRedraw();
    }
    if (canvasRef.current) canvasRef.current.style.cursor = node ? "pointer" : "grab";
  }, [findNodeAt, scheduleRedraw]);

  const handleMouseUp = useCallback((e: React.MouseEvent) => {
    const wasDragging = isDraggingRef.current;
    isDraggingRef.current = false;
    if (!wasDragging) return;
    const dx = e.clientX - dragStartRef.current.x;
    const dy = e.clientY - dragStartRef.current.y;
    if (Math.abs(dx) < 3 && Math.abs(dy) < 3) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (!rect) return;
      const sx = e.clientX - rect.left, sy = e.clientY - rect.top;
      const node = findNodeAt(sx, sy);
      onSelectNode(node?.id ?? null);
    }
  }, [findNodeAt, onSelectNode]);

  const handleDoubleClick = useCallback(() => {
    const simNodes = simNodesRef.current;
    if (!simNodes.length || !containerRef.current) return;
    let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    for (const n of simNodes) {
      minX = Math.min(minX, n.x);
      maxX = Math.max(maxX, n.x);
      minY = Math.min(minY, n.y);
      maxY = Math.max(maxY, n.y);
    }
    const w = containerRef.current.clientWidth, h = containerRef.current.clientHeight;
    const padding = 80;
    const gW = (maxX - minX) || 1, gH = (maxY - minY) || 1;
    const scale = Math.min((w - padding * 2) / gW, (h - padding * 2) / gH, 2);
    const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
    transformRef.current = { x: w / 2 - cx * scale, y: h / 2 - cy * scale, scale };
    scheduleRedraw();
  }, [scheduleRedraw]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const wheelHandler = (e: WheelEvent) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const sx = e.clientX - rect.left, sy = e.clientY - rect.top;
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      const newScale = Math.min(3, Math.max(0.2, transformRef.current.scale * delta));
      const ratio = newScale / transformRef.current.scale;
      transformRef.current.x = sx - (sx - transformRef.current.x) * ratio;
      transformRef.current.y = sy - (sy - transformRef.current.y) * ratio;
      transformRef.current.scale = newScale;
      scheduleRedraw();
    };
    canvas.addEventListener("wheel", wheelHandler, { passive: false });
    return () => canvas.removeEventListener("wheel", wheelHandler);
  }, [scheduleRedraw]);

  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;
      const w = container.clientWidth, h = container.clientHeight;
      canvas.width = w * 2;
      canvas.height = h * 2;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      draw();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [draw]);

  return (
    <div ref={containerRef} className="w-full h-[400px] lg:h-full relative" data-testid="prediction-brain-graph">
      <canvas
        ref={canvasRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => { isDraggingRef.current = false; hoveredNodeRef.current = null; scheduleRedraw(); }}
        onDoubleClick={handleDoubleClick}
        className="w-full h-full"
        style={{ cursor: "grab", touchAction: "none" }}
      />
      {nodes.length === 0 && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <p className="text-sm text-[#746F66]">No brain nodes yet</p>
            <p className="text-xs text-[#746F66] mt-1">Add chunks to build the Prediction Brain</p>
          </div>
        </div>
      )}
    </div>
  );
}
